import React, { useEffect, useState } from "react";
import { Alert, Col, Row } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { MultiSelectLiveDropdownPropsSelectItems } from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown.d";
import Popup from "../../../../components/Popup/Popup";
import translate from "../../../../services/Translate";
import { updateUserRoleScopes } from "../../PermissionUserRolesScopes.function";
import { fetchMeetingStaffs } from "./UserRoleScopeModal.function";

type UserRoleScopeModalProps = {
  show: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  language: string;
  userRole?: any;
};

const UserRoleScopeModal = ({ show, onClose, onSuccess, language, userRole }: UserRoleScopeModalProps) => {
  const isEdit = !!userRole;

  const initialUsersSelectedItems = isEdit
    ? (userRole?.users_scope || []).map((user: any) => ({
        category: "users",
        value: {
          ...user,
          first_name: user.display_name,
          last_name: "",
        },
      }))
    : [];

  const initialCohortsSelectedItems = isEdit
    ? (userRole?.cohorts_scope || []).map((cohort: any) => ({
        category: "cohorts",
        value: {
          ...cohort,
          name: cohort.display_name,
        },
      }))
    : [];

  const initialProjectsSelectedItems = isEdit
    ? (userRole?.project_groups_scope || []).map((project: any) => ({
        category: "projects",
        value: {
          ...project,
          name: project.display_name,
        },
      }))
    : [];

  const [selectedUser, setSelectedUser] = useState<MultiSelectLiveDropdownPropsSelectItems[]>([]);
  const [usersScope, setUsersScope] = useState<MultiSelectLiveDropdownPropsSelectItems[]>(initialUsersSelectedItems);
  const [organizersScope, setOrganizersScope] = useState<number[]>(
    isEdit ? userRole?.organizers_scope?.map((organizer: any) => organizer.id) || [] : []
  );
  const [animatorsScope, setAnimatorsScope] = useState<number[]>(
    isEdit ? userRole?.animators_scope?.map((animator: any) => animator.staff_member_id) || [] : []
  );
  const [cohortsScope, setCohortsScope] =
    useState<MultiSelectLiveDropdownPropsSelectItems[]>(initialCohortsSelectedItems);
  const [groupsScope, setGroupsScope] =
    useState<MultiSelectLiveDropdownPropsSelectItems[]>(initialProjectsSelectedItems);
  const [meetingStaffOptions, setMeetingStaffOptions] = useState<any>({
    animators: [],
    authors: [],
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState({
    meetingStaffs: false,
    projects: false,
  });
  const [nullifyUsers, setNullifyUsers] = useState(false);
  const [nullifyCohorts, setNullifyCohorts] = useState(false);
  const [nullifyProjects, setNullifyProjects] = useState(false);
  const [nullifyOrganizers, setNullifyOrganizers] = useState(false);
  const [nullifyAnimators, setNullifyAnimators] = useState(false);
  const allScopesNullified = nullifyUsers && nullifyCohorts && nullifyProjects && nullifyAnimators && nullifyOrganizers;

  useEffect(() => {
    if (show && !isEdit) {
      setSelectedUser([]);
      setUsersScope([]);
      setCohortsScope([]);
      setGroupsScope([]);
      setOrganizersScope([]);
      setAnimatorsScope([]);
      setNullifyUsers(false);
      setNullifyCohorts(false);
      setNullifyProjects(false);
      setNullifyOrganizers(false);
      setNullifyAnimators(false);
    }
  }, [show, isEdit]);

  useEffect(() => {
    if (!show) return;

    if (!isEdit) {
      setSelectedUser([]);
      setUsersScope([]);
      setCohortsScope([]);
      setGroupsScope([]);
      setOrganizersScope([]);
      setAnimatorsScope([]);
      setNullifyUsers(false);
      setNullifyCohorts(false);
      setNullifyProjects(false);
      setNullifyOrganizers(false);
      setNullifyAnimators(false);
    } else {
      setUsersScope(initialUsersSelectedItems);
      setCohortsScope(initialCohortsSelectedItems);
      setOrganizersScope(userRole?.organizers_scope?.map((organizer: any) => organizer.id) || []);
      setAnimatorsScope(userRole?.animators_scope?.map((animator: any) => animator.staff_member_id) || []);
      setGroupsScope(initialProjectsSelectedItems);
      setNullifyUsers(userRole?.users_scope === null);
      setNullifyCohorts(userRole?.cohorts_scope === null);
      setNullifyProjects(userRole?.project_groups_scope === null);
      setNullifyOrganizers(userRole?.organizers_scope === null);
      setNullifyAnimators(userRole?.animators_scope === null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isEdit, userRole]);

  useEffect(() => {
    fetchMeetingStaffs(setMeetingStaffOptions, language, setLoading);
  }, [language]);

  const _saveScope = () => {
    setSaving(true);

    const userId = isEdit ? userRole.user.id : selectedUser[0]?.value?.id;
    if (!userId) return;

    updateUserRoleScopes(
      userId,
      nullifyUsers ? null : usersScope.map((user: any) => user.value.id),
      nullifyCohorts ? null : cohortsScope.map((cohort: any) => cohort.value.id),
      nullifyProjects ? null : groupsScope.map((group) => group.value.id),
      nullifyOrganizers ? null : organizersScope,
      nullifyAnimators ? null : animatorsScope,
      () => {
        onSuccess?.();
        onClose();
      },
      language,
      setSaving,
      isEdit
    );
  };

  const _renderScopeLinks = (items: any, basePath: string) => {
    if (items === null) return <span>{translate(language, "PAGES.USER_ROLES_SCOPES.VALUES_ALIAS.ALL")}</span>;
    if (!items?.length) return <span>{translate(language, "PAGES.USER_ROLES_SCOPES.VALUES_ALIAS.NONE")}</span>;
    return (
      <span>
        {items.map((item: any, index: number) => (
          <React.Fragment key={item.id}>
            <a
              href={`/${basePath}/${item.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
              title={item.id}
            >
              {item.display_name}
            </a>
            {index < items.length - 1 ? ", " : ""}
          </React.Fragment>
        ))}
      </span>
    );
  };

  return (
    <Popup
      show={show}
      fullscreen
      onClose={onClose}
      modalTitle={
        isEdit ? (
          <React.Fragment>
            {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.TITLE_EDIT")}
            <a
              href={`/user/${userRole?.user?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ms-1 text-decoration-none"
            >
              {`${userRole?.user?.first_name} ${userRole?.user?.last_name}`}
            </a>
          </React.Fragment>
        ) : (
          translate(language, "PAGES.USER_ROLES_SCOPES.ADD_SCOPE")
        )
      }
      size="xl"
      footerContent={
        <React.Fragment>
          {allScopesNullified && (
            <p className="my-0 red-font">
              {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.ADD_SCOPE_ALL_NULLIFIED")}
            </p>
          )}
          <DstButton
            value={translate(language, "GLOBAL.CANCEL")}
            variant="secondary"
            clickFunction={onClose}
            disabled={saving}
          />
          <DstButton
            value={translate(language, "GLOBAL.SAVE")}
            variant="primary"
            btnClass="btn btn-primary"
            clickFunction={_saveScope}
            disabled={
              saving ||
              loading.projects ||
              loading.meetingStaffs ||
              (!isEdit && (!selectedUser.length || allScopesNullified))
            }
            loading={saving}
          />
        </React.Fragment>
      }
    >
      <div className="d-flex flex-column gap-3 px-3">
        {!isEdit && (
          <Alert variant="warning" className="mb-0">
            {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.WARNING_ERASE_DATA")}
          </Alert>
        )}
        {!isEdit && (
          <div>
            <label className="form-label">
              {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.TARGET_USER")}
            </label>
            <MultiSelectLiveDropdown
              category={["users"]}
              isSingleSelect
              label={
                selectedUser.length
                  ? `${selectedUser[0].value.first_name} ${selectedUser[0].value.last_name}`
                  : translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.SELECT_TARGET_USER")
              }
              language={language}
              onConfirm={setSelectedUser}
              initialSelectedItems={selectedUser}
              toggleClass="min-w-100px m-regular border w-100"
            />
          </div>
        )}
        {isEdit && (
          <div className="pb-3 border-bottom">
            <h3>{translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.PREVIOUS_SCOPE")}</h3>
            <div className="px-2">
              <div>
                <strong>{translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.USERS_SCOPE")}</strong>
                <div>{_renderScopeLinks(userRole?.users_scope, "user")}</div>
              </div>
              <div className="mt-3">
                <strong>{translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.COHORTS_SCOPE")}</strong>
                <div>{_renderScopeLinks(userRole?.cohorts_scope, "cohort")}</div>
              </div>
              <div className="mt-3">
                <strong>{translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.PROJECT_GROUPS_SCOPE")}</strong>
                <div>{_renderScopeLinks(userRole?.project_groups_scope, "project")}</div>
              </div>
              <div className="mt-3">
                <strong>{translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.ORGANIZERS_SCOPE")}</strong>
                <div>{_renderScopeLinks(userRole?.organizers_scope, "user")}</div>
              </div>
              <div className="mt-3">
                <strong>{translate(language, "PAGES.USER_ROLES_SCOPES.COLUMNS.ANIMATORS_SCOPE")}</strong>
                <div>{_renderScopeLinks(userRole?.animators_scope, "user")}</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <h3>{translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.DESIRED_SCOPE")}</h3>
          <Row className="px-2">
            <Col lg={4}>
              <label className="form-label">
                {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.USERS_SCOPE")}
              </label>
              <MultiSelectLiveDropdown
                category={["users"]}
                label={
                  usersScope.length > 0
                    ? `${usersScope.length} ${translate(language, "COMPONENTS.MULTI_SELECT.ITEMS")} ${translate(
                        language,
                        "COMPONENTS.MULTI_SELECT.SELECTED"
                      )}`
                    : translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.SELECT_USERS_SCOPE")
                }
                language={language}
                onConfirm={setUsersScope}
                initialSelectedItems={usersScope}
                toggleClass="min-w-100px m-regular border w-100"
                disabled={nullifyUsers}
              />
              <div className="form-check mt-1 s-regular lh-base">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="nullifyUsers"
                  checked={nullifyUsers}
                  onChange={() => setNullifyUsers(!nullifyUsers)}
                />
                <label className="form-check-label" htmlFor="nullifyUsers">
                  {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.NULLIFY_USERS_SCOPE")}
                </label>
              </div>
            </Col>
            <Col lg={4}>
              <label className="form-label">
                {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.COHORTS_SCOPE")}
              </label>
              <MultiSelectLiveDropdown
                category={["cohorts"]}
                label={
                  cohortsScope.length > 0
                    ? `${cohortsScope.length} ${translate(language, "COMPONENTS.MULTI_SELECT.ITEMS")} ${translate(
                        language,
                        "COMPONENTS.MULTI_SELECT.SELECTED"
                      )}`
                    : translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.SELECT_COHORTS_SCOPE")
                }
                language={language}
                onConfirm={setCohortsScope}
                initialSelectedItems={cohortsScope}
                toggleClass="min-w-100px m-regular border w-100"
                disabled={nullifyCohorts}
              />
              <div className="form-check mt-1 s-regular lh-base">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="nullifyCohorts"
                  checked={nullifyCohorts}
                  onChange={() => setNullifyCohorts(!nullifyCohorts)}
                />
                <label className="form-check-label" htmlFor="nullifyCohorts">
                  {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.NULLIFY_COHORTS_SCOPE")}
                </label>
              </div>
            </Col>
            <Col lg={4}>
              <label className="form-label">
                {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.PROJECTS_SCOPE")}
              </label>
              <MultiSelectLiveDropdown
                category={["projects"]}
                label={
                  groupsScope.length > 0
                    ? `${groupsScope.length} ${translate(language, "COMPONENTS.MULTI_SELECT.ITEMS")} ${translate(
                        language,
                        "COMPONENTS.MULTI_SELECT.SELECTED"
                      )}`
                    : translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.SELECT_PROJECTS_SCOPE")
                }
                language={language}
                onConfirm={setGroupsScope}
                initialSelectedItems={groupsScope}
                toggleClass="min-w-100px m-regular border w-100"
                disabled={nullifyProjects}
                mock={loading.projects}
              />
              <div className="form-check mt-1 s-regular lh-base">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="nullifyProjects"
                  checked={nullifyProjects}
                  onChange={() => setNullifyProjects(!nullifyProjects)}
                />
                <label className="form-check-label" htmlFor="nullifyProjects">
                  {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.NULLIFY_PROJECTS_SCOPE")}
                </label>
              </div>
            </Col>

            <Col lg={4}>
              <label className="form-label">
                {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.ORGANIZERS_SCOPE")}
              </label>

              <MultiSelectDropdown
                toggleClass="m-regular w-100"
                items={meetingStaffOptions.authors}
                defaultSelectedItems={isEdit ? userRole?.organizers_scope || [] : []}
                onConfirm={(selected) => setOrganizersScope(selected.map((organizer) => organizer.id))}
                badgeItemProperty="id"
                displayItem={(item) => <React.Fragment>{item.full_name}</React.Fragment>}
                language={language}
                label={
                  organizersScope.length > 0
                    ? `${organizersScope.length} ${translate(language, "COMPONENTS.MULTI_SELECT.ITEMS")} ${translate(
                        language,
                        "COMPONENTS.MULTI_SELECT.SELECTED"
                      )}`
                    : translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.SELECT_ORGANIZERS_SCOPE")
                }
                borderToggle
                showAlwaysConfirm
                disabled={nullifyOrganizers}
                mock={loading.meetingStaffs}
                btnWidth="100%"
              />

              <div className="form-check mt-1 s-regular lh-base">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="nullifyOrganizers"
                  checked={nullifyOrganizers}
                  onChange={() => setNullifyOrganizers(!nullifyOrganizers)}
                />
                <label className="form-check-label" htmlFor="nullifyOrganizers">
                  {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.NULLIFY_ORGANIZERS_SCOPE")}
                </label>
              </div>
            </Col>

            <Col lg={4}>
              <label className="form-label">
                {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.ANIMATORS_SCOPE")}
              </label>

              <MultiSelectDropdown
                toggleClass="m-regular w-100"
                items={meetingStaffOptions.animators}
                defaultSelectedItems={isEdit ? userRole?.animators_scope || [] : []}
                onConfirm={(selected) => setAnimatorsScope(selected.map((animator) => animator.staff_member_id))}
                badgeItemProperty="id"
                displayItem={(item) => <React.Fragment>{item.full_name}</React.Fragment>}
                language={language}
                label={
                  animatorsScope.length > 0
                    ? `${animatorsScope.length} ${translate(language, "COMPONENTS.MULTI_SELECT.ITEMS")} ${translate(
                        language,
                        "COMPONENTS.MULTI_SELECT.SELECTED"
                      )}`
                    : translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.SELECT_ANIMATORS_SCOPE")
                }
                borderToggle
                showAlwaysConfirm
                disabled={nullifyAnimators}
                mock={loading.meetingStaffs}
                btnWidth="100%"
              />

              <div className="form-check mt-1 s-regular lh-base">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="nullifyAnimators"
                  checked={nullifyAnimators}
                  onChange={() => setNullifyAnimators(!nullifyAnimators)}
                />
                <label className="form-check-label" htmlFor="nullifyAnimators">
                  {translate(language, "PAGES.USER_ROLES_SCOPES.SCOPE_MODAL.NULLIFY_ANIMATORS_SCOPE")}
                </label>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Popup>
  );
};

export default UserRoleScopeModal;
